import * as React from "react";
import {
  Input,
  Dropdown,
  Option,
  makeStyles,
  tokens,
  Button,
  Text,
} from "@fluentui/react-components";
import { Search24Regular, Dismiss24Regular, ArrowClockwise24Regular } from "@fluentui/react-icons";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  filterRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchInput: {
    flex: "1 1 200px",
    minWidth: "150px",
  },
  dropdown: {
    minWidth: "120px",
  },
  resultCountContainer: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
});

interface TaskFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  outputTypeFilter: string;
  onOutputTypeFilterChange: (value: string) => void;
  taskTypeFilter: string;
  onTaskTypeFilterChange: (value: string) => void;
  sortBy: string;
  onSortByChange: (value: string) => void;
  resultCount: number;
  onClearFilters: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  availableOutputTypes: string[];
  availableTaskTypes: string[];
}

const TaskFilters: React.FC<TaskFiltersProps> = ({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  outputTypeFilter,
  onOutputTypeFilterChange,
  taskTypeFilter,
  onTaskTypeFilterChange,
  sortBy,
  onSortByChange,
  resultCount,
  onClearFilters,
  onRefresh,
  isRefreshing,
  availableOutputTypes,
  availableTaskTypes,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();

  const hasFilters =
    search !== "" ||
    statusFilter !== "all" ||
    outputTypeFilter !== "all" ||
    taskTypeFilter !== "all";

  return (
    <div className={styles.container}>
      <div className={styles.filterRow}>
        <Input
          className={styles.searchInput}
          placeholder="Search agents..."
          contentBefore={<Search24Regular />}
          value={search}
          onChange={(_, data) => onSearchChange(data.value)}
        />

        <Dropdown
          className={styles.dropdown}
          placeholder="Status"
          value={statusFilter === "all" ? "All Statuses" : statusFilter}
          onOptionSelect={(_, data) =>
            onStatusFilterChange(data.optionValue || "all")
          }
        >
          <Option value="all">All Statuses</Option>
          <Option value="to_do">To Do</Option>
          <Option value="in_progress">In Progress</Option>
          <Option value="incomplete">Incomplete</Option>
          <Option value="complete">Complete</Option>
        </Dropdown>

        {availableOutputTypes.length > 0 && (
          <Dropdown
            className={styles.dropdown}
            placeholder="Output Type"
            value={outputTypeFilter === "all" ? "All Output Types" : outputTypeFilter}
            onOptionSelect={(_, data) =>
              onOutputTypeFilterChange(data.optionValue || "all")
            }
          >
            <Option value="all">All Output Types</Option>
            {availableOutputTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Dropdown>
        )}

        {availableTaskTypes.length > 0 && (
          <Dropdown
            className={styles.dropdown}
            placeholder="Agent Type"
            value={taskTypeFilter === "all" ? "All Agent Types" : taskTypeFilter}
            onOptionSelect={(_, data) =>
              onTaskTypeFilterChange(data.optionValue || "all")
            }
          >
            <Option value="all">All Agent Types</Option>
            {availableTaskTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Dropdown>
        )}

        <Dropdown
          className={styles.dropdown}
          placeholder="Sort by"
          value={
            sortBy === "newest"
              ? "Newest"
              : sortBy === "oldest"
              ? "Oldest"
              : sortBy === "updated"
              ? "Recently Updated"
              : "Name"
          }
          onOptionSelect={(_, data) => onSortByChange(data.optionValue || "newest")}
        >
          <Option value="newest">Newest</Option>
          <Option value="oldest">Oldest</Option>
          <Option value="updated">Recently Updated</Option>
          <Option value="name">Name</Option>
        </Dropdown>

        {hasFilters && (
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={onClearFilters}
          >
            Clear
          </Button>
        )}

        <div className={styles.resultCountContainer}>
          <Text className={typography.caption}>
            {resultCount} {resultCount === 1 ? "agent" : "agents"}
          </Text>
          <Button
            appearance="subtle"
            icon={<ArrowClockwise24Regular />}
            onClick={onRefresh}
            disabled={isRefreshing}
            size="small"
            title="Refresh agents"
          />
        </div>
      </div>
    </div>
  );
};

export default TaskFilters;
